/* eslint-disable security/detect-object-injection */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    IItemAddedToCartDialogData,
    IItemAddedToCartDialogResources,
    IOrderTemplateNameDialogResources,
    ItemAddedToCartDialog
} from '@msdyn365-commerce/components';
import { ILinesAddedToCartDialogResources } from './components/lines-added-to-cart-dialog.component';
import MsDyn365, { AsyncResult, getCatalogId, getUrlSync, IRequestContext } from '@msdyn365-commerce/core';
import { ICartActionResult } from '@msdyn365-commerce/global-state';
import { ProductList, ProductListLine } from '@msdyn365-commerce/retail-proxy';
import {
    deleteAsync,
    removeProductListLinesAsync,
    updateProductListLinesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import { Customer, FeatureState, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { Waiting } from '@msdyn365-commerce-modules/utilities';
import {
    ArrayExtensions,
    createGetFeatureStateInput,
    getFeatureState,
    getPriceForSelectedVariant,
    //getProductUrlSync,
    ObjectExtensions,
    OrderTemplate,
    OrderTemplateLine,
    PriceForSelectedVariantInput,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Alert,
    getTelemetryObject,
    Heading,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    UncontrolledPagination
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    IOrderTemplateLineProps,
    IOrderTemplateLineViewProps,
    IOrderTemplateTableHeadingProps,
    OrderTemplateLinesStatusMessage
} from '@msdyn365-commerce-modules/order-template';
import { OrderTemplateActionsView, IOrderTemplateActionsProps, IOrderTemplateActionsViewProps } from './components';
import { OrderTemplateTableHeading } from './components/krannich-c-order-template-table-heading';

import {
    OrderTemplateLineView,
    OrderTemplateMobileLineView
} from '../../modules/krannich-c-order-template/components/krannich-c-order-template-lines';
import { ActiveOrderTemplateLinesInput } from './actions/get-krannich-c-order-template';
import { OrderTemplateBulkActions } from './components/bulk-actions';
import { IKrannichCOrderTemplateData } from './krannich-c-order-template.data';
import { IKrannichCOrderTemplateProps, IKrannichCOrderTemplateResources } from './krannich-c-order-template.props.autogenerated';
import { kRV_GetMultipleATPLeadTimeDateAsync } from '../../actions/customProxy/ATP-update/DataActionExtension.g';

import { ICartState } from '@msdyn365-commerce/global-state';
import { getActivePricesAsync, getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { formatUrlPath, isRequestPrice } from '../../utils/helper';

export interface IOrderTemplateLineActionMessageState {
    itemKey: string;
    userMessage: string;
    statusCssString: string;
    messageGlyph: string;
    isOpen: boolean;
    addToCartStatus: orderTemplateLinesStatus;
    isItemAddedToOrderTemplateOpen: boolean;
    isConfirmAddSelectionToBagModalOpen: boolean;
    itemAddedToCartDialogData?: IItemAddedToCartDialogData;
    productSelection: IOrderTemplateLineViewModel[];
    isDetailsDisplayed: boolean;
    detailedProductView?: IOrderTemplateLineViewProps;
    isQuantityDisabledForUpdate?: boolean;
    isSelectedProdPopUpShow: boolean;
    addedLinesToPassArr: IOrderTemplateLineViewModel[];
    addedLinesToPassArrSuccessfull?: any[];
    isAllselectedItemsAddedSuccessfully?: boolean;
    atpDataForKits: any;
    refinedAtpData: any;
    refinedOrderTemplateLines: any;
    activePrices: any;
    tempOrderTemplate: any;
    cartLinesState?: any;
    attributeValues?: any;
}

export type orderTemplateLinesStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY' | 'FAILEDBYZEROPRICE';
type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IOrderTemplateLinePropsExtended extends IOrderTemplateLineProps {
    kitDiscontinuedFlagValue: boolean;
    customer: AsyncResult<Customer> | undefined;
    maxAtpValue?: number;
    selectRowText?: string;
    requestPriceText?: string;
    requestPriceTooltipText?: string;
    discontinuedText?: string;
    minPurchaseQuantityIsText?: string;
    maxPurchaseQuantityIsText?: string;
    informationText?: string;
}
export interface IOrderTemplateViewProps extends IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData> {
    OrderTemplateLines: IModuleProps;
    callbacks?: IOrderTemplateLinesCallbacks;
    status?: orderTemplateLinesStatus;
    statusMessage?: React.ReactNode;
    heading?: React.ReactNode;
    OrderTemplateActions?: INodeProps;
    ProductsTable?: INodeProps;
    ProductsTableRow?: INodeProps;
    ProductsTableHeading?: React.ReactNode;
    ProductDetails?: INodeProps;
    orderLines?: IOrderTemplateLineViewProps[];
    productListLines?: ProductListLine[];
    productsStatusMessage?: React.ReactNode;
    orderActions?: IOrderTemplateActionsViewProps | null;
    resources: IKrannichCOrderTemplateResources;
    pagination?: React.ReactNode;
    itemAddedToCartDialog?: React.ReactNode;
    bulkOrderLineActions?: React.ReactNode;
    isDetailsDisplayed?: boolean;
    detailedProduct?: IOrderTemplateLineProps;
    detailedProductView?: IOrderTemplateLineViewProps;
    productQuantityView?: React.ReactNode;
    addButton?: React.ReactNode;
    isMobile?: boolean;
    toggleDetailsDisplayed?(props: IOrderTemplateLineProps | undefined): void;
    isSelectedProdPopUpShow: boolean;
    cartLinesState?: any;
    addedLinesToPassArr: IOrderTemplateLineViewModel[];
    addedLinesToPassArrSuccessfull?: any[];
    isAllselectedItemsAddedSuccessfully?: boolean;
    _handlerToClearSelectedPopUp: () => void;
}

export interface IOrderTemplateLinesCallbacks {
    updateQuantity(itemKey: string, quantity: number): void;
}

export interface IOrderTemplateLineViewModel extends OrderTemplateLine {
    isChecked: boolean;
    productQuantityView: React.ReactNode;
}

/**
 *
 * OrderTemplateLines component.
 * @extends {React.PureComponent<IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>, IOrderTemplateLineActionMessageState>}
 */
@observer
class OrderTemplateLines extends React.Component<
    IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>,
    IOrderTemplateLineActionMessageState
> {
    private readonly className: string;

    private readonly telemetryContent?: ITelemetryContent;

    @observable
    private _viewport: GridSize;

    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    constructor(props: IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>, state: IOrderTemplateLineActionMessageState) {
        super(props);
        this.className = 'ms-order-template';
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._updateViewport = this._updateViewport.bind(this);

        this.state = {
            itemKey: '',
            userMessage: '',
            statusCssString: 'success',
            messageGlyph: 'fas fa-check',
            addToCartStatus: 'LOADING',
            isOpen: false,
            isItemAddedToOrderTemplateOpen: false,
            isConfirmAddSelectionToBagModalOpen: false,
            productSelection: [],
            isDetailsDisplayed: false,
            isQuantityDisabledForUpdate: false,
            isSelectedProdPopUpShow: false,
            addedLinesToPassArr: [],
            addedLinesToPassArrSuccessfull: [],
            isAllselectedItemsAddedSuccessfully: false,
            atpDataForKits: [],
            refinedAtpData: [],
            refinedOrderTemplateLines: [],
            activePrices: [],
            tempOrderTemplate: [],
            attributeValues: []
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this._updateViewport();

        if (MsDyn365.isBrowser && !this.props.context.request.user.isAuthenticated && !this.props.context.request.params.isEditor) {
            window.location.assign(this.props.context.request.user.signInUrl || '/');
        }
    }

    @action
    public async componentDidMount(): Promise<void> {
        this.setState({});
        const orderTemplate = await this.props.data.orderTemplate;
        const cartLines = (await this.props.data.cart)?.cart.CartLines;
        this.setState({ tempOrderTemplate: orderTemplate, cartLinesState: cartLines });
        this.props.config.heading!.text = orderTemplate?.productList.Name || '';
        //filtering undefined excluded products
        const filtered = orderTemplate?.orderTemplateLines?.filter(item => item.fullProduct !== undefined);
        //setting new order template
        const custom = this.state.tempOrderTemplate;
        custom.orderTemplateLines = filtered as IOrderTemplateLineViewModel[];
        custom.totalLines = filtered?.length;

        this.setState({ tempOrderTemplate: custom, productSelection: filtered as IOrderTemplateLineViewModel[] }, () => {
            void this._atpData().then(() => {
                orderTemplate && this._refiningOrderTemplateLines(this.state.tempOrderTemplate, this.props.data.customerInformation);
            });
        });
        void this._attributesData();

        if (MsDyn365.isBrowser) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public shouldComponentUpdate(
        nextProps: IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>,
        nextState: IOrderTemplateLineActionMessageState
    ): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const {
            // emptyOrderTemplateLinesText,
            errorGettingOrderTemplateLines
        } = this.props.resources;
        const { orderTemplate, cart } = this.props.data;

        if (cart?.status === 'LOADING' || ObjectExtensions.isNullOrUndefined(orderTemplate) || orderTemplate.status === 'LOADING') {
            const errorProps: IOrderTemplateViewProps = {
                ...(this.props as IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(this.className, className)
                },
                status: 'LOADING',
                statusMessage: <Waiting />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources,
                isSelectedProdPopUpShow: this.state.isSelectedProdPopUpShow,
                addedLinesToPassArr: [],
                _handlerToClearSelectedPopUp: this._handlerToClearSelectedPopUp
            };
            return this.props.renderView(errorProps);
        }

        if (cart?.status === 'FAILED' || orderTemplate.status === 'FAILED') {
            const errorProps: IOrderTemplateViewProps = {
                ...(this.props as IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(`${this.className}`, className)
                },
                status: 'FAILED',
                statusMessage: <OrderTemplateLinesStatusMessage errorType='FAILED' text={errorGettingOrderTemplateLines} />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources,
                isSelectedProdPopUpShow: this.state.isSelectedProdPopUpShow,
                addedLinesToPassArr: [],
                _handlerToClearSelectedPopUp: this._handlerToClearSelectedPopUp
            };

            return this.props.renderView(errorProps);
        }

        const orderTemplateResult = orderTemplate && orderTemplate.result;

        // condition to disable AddTemplateToCart button incase of only Discontinued/RequestPrice items left in template
        if (this.state.refinedOrderTemplateLines && this.state.refinedOrderTemplateLines.length === 0) {
            orderTemplateResult!.totalLines = 0;
        }

        const viewProps: IOrderTemplateViewProps = {
            ...(this.props as IKrannichCOrderTemplateProps<IKrannichCOrderTemplateData>),
            OrderTemplateLines: {
                moduleProps: this.props,
                className: classnames(`${this.className}`, className)
            },
            status: this.state.addToCartStatus,
            productsStatusMessage: this.state.isOpen && (
                <div className={`${this.className}-products-status`}>
                    <Alert color={this.state.statusCssString} isOpen={this.state.isOpen} toggle={this._onDismiss}>
                        <span aria-live='assertive'>{this.state.userMessage}</span>
                    </Alert>
                </div>
            ),
            heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
            OrderTemplateActions: {
                className: `${this.className}-action-bar`,
                tag: 'div'
            },
            ProductsTable: {
                className: `${this.className}__table`,
                tag: 'table',
                role: 'grid'
            },
            ProductsTableRow: {
                className: `${this.className}-table-line`,
                tag: 'tr'
            },
            ProductsTableHeading: this._renderProductListHeader(this.props.resources),
            ProductDetails: {
                className: `${this.className}-table-line__link`,
                tag: 'a'
            },
            orderLines: this._renderProductList(),
            callbacks: { updateQuantity: debounce(this._changeQuantity, 2000) },
            orderActions:
                orderTemplateResult &&
                this._renderOrderTemplateActions(
                    this.state.refinedOrderTemplateLines.length === 0 ? orderTemplateResult : this.state.refinedOrderTemplateLines,
                    cart?.result,
                    this._isChannelMultipleCatalogsFeatureEnabled()
                ),
            pagination: this._renderPagination(),
            itemAddedToCartDialog: this._renderItemAddedToCartDialog(),
            bulkOrderLineActions: orderTemplateResult && this._getOrderTemplateLineActions(orderTemplateResult.productList.Id),
            isDetailsDisplayed: this.state.isDetailsDisplayed,
            toggleDetailsDisplayed: this._toggleDetailsDisplayed,
            detailedProductView: this.state.detailedProductView,
            isMobile: this.isMobile,
            isSelectedProdPopUpShow: this.state.isSelectedProdPopUpShow,
            addedLinesToPassArr: this.state.addedLinesToPassArr,
            addedLinesToPassArrSuccessfull: this.state.addedLinesToPassArrSuccessfull,
            isAllselectedItemsAddedSuccessfully: this.state.isAllselectedItemsAddedSuccessfully,
            _handlerToClearSelectedPopUp: this._handlerToClearSelectedPopUp
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public onSelect(line: IOrderTemplateLineViewModel, isChecked: boolean): void {
        line.isChecked = isChecked;

        const updated =
            this.state.productSelection.map(product => {
                if (product.fullProduct?.ProductDetails.ItemId === line.fullProduct?.ProductDetails.ItemId) {
                    line.isChecked = isChecked;
                }
                return product;
            }) || [];

        this.setState({
            productSelection: updated
        });
    }

    public onSelectAll(isChecked: boolean): void {
        const updated =
            this.state.productSelection.map(line => {
                line.isChecked = isChecked;
                return line;
            }) || [];

        this.setState({
            productSelection: updated
        });
    }

    private _renderProductListHeader(resources: IKrannichCOrderTemplateResources): React.ReactNode {
        const {
            productNumberText,
            productText,
            productUnitPriceText,
            productUOMText,
            productQuantityText,
            productActionsText,
            selectAllRowsText,
            productLineCatalogName
        } = resources;
        const headers: IOrderTemplateTableHeadingProps = {
            productNumber: productNumberText,
            productName: productText,
            productUnitPrice: productUnitPriceText,
            productUOM: productUOMText,
            productQuantity: productQuantityText,
            productActions: productActionsText,
            selectAllRowsText,
            lines: this.state.productSelection,
            isMobile: this.isMobile,
            clickHandler: (isChecked: boolean) => {
                this.onSelectAll(isChecked);
            },
            isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled(),
            productLineCatalogName: productLineCatalogName
        };

        return <OrderTemplateTableHeading {...headers} />;
    }

    private _getOrderTemplateLineActions(orderTemplateId: string): React.ReactNode {
        return (
            <>
                <div className='order-template-btns'>
                    <OrderTemplateBulkActions
                        {...this.props}
                        orderTemplateId={orderTemplateId}
                        selectedLines={this.state.productSelection && this.state.productSelection.filter(line => line.isChecked)}
                        onAddSelectionToBag={this._onAddSelectionToBag}
                        onRemoveSelection={this._onRemoveSelection}
                        isMinifiedView={this.isMobile}
                        productSelection={this.state.productSelection}
                        isMultipleCatalogFeatureEnabledInHq={this._isChannelMultipleCatalogsFeatureEnabled()}
                        telemetryContent={this.telemetryContent}
                        //VSI CUSTOMIZATION
                    />
                </div>
            </>
        );
    }

    private _generateLinesAddedToCartDialogResources(): ILinesAddedToCartDialogResources {
        return {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            linesAddedToCartHeaderItemsOneText: this.props.resources.linesAddedToCartHeaderItemsOneText,
            linesAddedToCartHeaderLinesOneText: this.props.resources.linesAddedToCartHeaderLinesOneText,
            linesAddedToCartHeaderLinesFormatText: this.props.resources.linesAddedToCartHeaderLinesFormatText,
            linesAddedToCartHeaderMessageText: this.props.resources.linesAddedToCartHeaderMessageText,
            onlyThoselinesAddedToCartMessageText: this.props.resources.onlyThoselinesAddedToCartMessageText
        };
    }

    private _generateItemAddedToCartDialogResources(): IItemAddedToCartDialogResources {
        return {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            itemAddedToCartHeaderItemOneText: this.props.resources.itemAddedToCartHeaderItemOneText,
            itemAddedToCartHeaderItemFormatText: this.props.resources.itemAddedToCartHeaderItemFormatText,
            itemAddedToCartHeaderMessageText: this.props.resources.itemAddedToCartHeaderMessageText,
            freePriceText: this.props.resources.freePriceText,
            originalPriceText: this.props.resources.originalPriceText,
            currentPriceText: this.props.resources.currentPriceText
        };
    }
    private readonly _isChannelMultipleCatalogsFeatureEnabled = (): boolean => {
        let isMultipleCatalogFeatureEnabledInHq = false;
        if (this.props.data.featureState) {
            isMultipleCatalogFeatureEnabledInHq =
                this.props.data.featureState.result?.find(
                    feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                )?.IsEnabled || false;
        }
        return isMultipleCatalogFeatureEnabledInHq;
    };
    private readonly _onRemoveSelection = (): void => {
        const { context, resources } = this.props;
        const { telemetry } = context;
        const { removeSelectedFailureMessage, removeSelectedSuccessMessage } = resources;
        const lines = this.state.productSelection;
        const productListLines = lines && lines.length && lines.filter(line => line.isChecked).map(line => line.productListLine);

        if (productListLines && productListLines.length > 0) {
            const productListId = productListLines[0].ProductListId;

            const maxWaitTimeInMilliSeconds: number = 1000;
            removeProductListLinesAsync({ callerContext: context.actionContext }, productListId!, productListLines)
                .then(async () => {
                    this.setState({
                        userMessage: removeSelectedSuccessMessage,
                        statusCssString: 'success',
                        messageGlyph: 'fas fa-check',
                        isOpen: true
                    });
                    //for closing alert
                    setTimeout(() => {
                        this.setState({
                            isOpen: false
                        });
                    }, 5000);
                    // eslint-disable-next-line no-promise-executor-return -- fix
                    await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));
                    window.location.reload();
                })
                .catch(error => {
                    telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                    telemetry.error(error);
                    this.setState({
                        userMessage: removeSelectedFailureMessage,
                        statusCssString: 'success',
                        messageGlyph: 'fas fa-check',
                        isOpen: true
                    });
                    //for closing alert
                    setTimeout(() => {
                        this.setState({
                            isOpen: false
                        });
                    }, 5000);
                });
        }
    };

    // private readonly _getOrderTemplatePageUrl = (): string => {
    //     let url = getUrlSync('ordertemplate', this.props.context.actionContext) || '';
    //     url = url.includes('?') ? url.substr(0, url.indexOf('?')) : url;
    //     if (this.props.data.orderTemplate?.result?.productList.Id) {
    //         return `${url}?id=${this.props.data.orderTemplate.result.productList.Id}`;
    //     }
    //     return '';
    // };

    private readonly _onAddSelectionToBag = async (): Promise<void> => {
        const lines = this.state.productSelection;
        const selectedLines = lines.filter(line => line.isChecked);
        if (selectedLines.length > 0) {
            for (const productLine of selectedLines) {
                const itemKey = productLine.fullProduct?.ProductDetails.ItemId ?? '';
                const product = productLine.fullProduct?.ProductDetails ?? {
                    RecordId: 0,
                    ProductTypeValue: 0,
                    BasePrice: 0,
                    Price: 0,
                    AdjustedPrice: 0
                };
                const quantity = productLine.productListLine.Quantity ?? 0;
                if (this._isChannelMultipleCatalogsFeatureEnabled()) {
                    const catalogId = productLine.productListLine.CatalogId;
                    await this._addItemToBag(product, quantity, itemKey, catalogId);
                } else {
                    await this._addItemToBag(product, quantity, itemKey);
                }
            }

            if (selectedLines.length !== this.state.addedLinesToPassArrSuccessfull?.length) {
                this.setState({ isAllselectedItemsAddedSuccessfully: true });
            }
            this.setState({
                isSelectedProdPopUpShow: true,
                addedLinesToPassArr: selectedLines
            });
        }
        return Promise.resolve(void 0);
    };
    //VSI CUSTOMIZATION Start TO CLEAR addedLinesToPassArr & false  isSelectedProdPopUpShow
    public _handlerToClearSelectedPopUp = () => {
        this.setState({
            isSelectedProdPopUpShow: false,
            addedLinesToPassArr: [],
            addedLinesToPassArrSuccessfull: [],
            isAllselectedItemsAddedSuccessfully: false
        });
    };
    //VSI Customization End
    private _renderOrderTemplateActions(
        orderTemplate: OrderTemplate,
        cart: ICartState | undefined,
        isMultipleCatalogFeatureEnabledInHq: boolean
    ): IOrderTemplateActionsViewProps {
        const { id, typeName, context, resources } = this.props;
        const {
            addTemplateToBagButtonText,
            renameTemplateButtonText,
            deleteTemplateButtonText,
            closeWindowButtonText,
            addToCartFailureMessage,
            addToCartSuccessMessage,
            addToCartProcessMessage,
            orderTemplateErrorMessage
        } = resources;

        const orderTemplateActions: IOrderTemplateActionsProps = {
            data: { orderTemplate, cart },
            atpData: this.state.refinedAtpData,
            id,
            typeName,
            context,
            telemetryContent: this.telemetryContent,
            handlers: {
                onRenameTemplate: this._renameTemplate,
                onRemoveTemplate: this._removeTemplate
            },
            isMultipleCatalogFeatureEnabledInHq,
            renameOrderTemplateDialogStrings: this._generateOrderTemplateNameDialogResources(resources),
            resources: {
                addTemplateToBagButtonText,
                renameTemplateButtonText,
                deleteTemplateButtonText,
                addToCartFailureMessage,
                addToCartSuccessMessage,
                addToCartProcessMessage,
                closeWindowButtonText,
                orderTemplateErrorMessage,
                linesAddedToCartDialogResources: this._generateLinesAddedToCartDialogResources()
            }
        };
        return OrderTemplateActionsView(orderTemplateActions);
    }

    private _generateOrderTemplateNameDialogResources(resources: IKrannichCOrderTemplateResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.renameOrderTemplateTitleText,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateTitle,
            orderTemplateDescription: '',
            defaultOrderTemplateName: resources.defaultOrderTemplateName,
            orderTemplateButtonLabel: resources.renameOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelBtnLabel
        };
    }

    private readonly _getCurrentUrl = (reqContext: IRequestContext): URL => {
        if (MsDyn365.isBrowser) {
            return new URL(window.location.href);
        }
        return new URL(reqContext.url.requestUrl.href);
    };

    /**
     * On close handler.
     */
    private readonly onCloseHandler = () => {
        this.setState({
            itemKey: this.state.itemKey,
            userMessage: this.state.userMessage,
            statusCssString: this.state.statusCssString,
            messageGlyph: this.state.messageGlyph,
            isOpen: this.state.isOpen,
            isItemAddedToOrderTemplateOpen: false
        });
    };

    private readonly _renderItemAddedToCartDialog = (): React.ReactNode | null => {
        if (!this.state.itemAddedToCartDialogData) {
            return null;
        }
        const isMultipleCatalogFeatureEnabledInHq =
            this.props.data.featureState?.result?.find(feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')
                ?.IsEnabled || false;
        return (
            <ItemAddedToCartDialog
                className='msc-item-added-to-cart-dialog'
                id={this.props.id}
                typeName={this.props.typeName}
                context={this.props.context}
                dialogStrings={this._generateItemAddedToCartDialogResources()}
                data={this.state.itemAddedToCartDialogData}
                modalOpen={this.state.isItemAddedToOrderTemplateOpen}
                onClose={this.onCloseHandler}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
            />
        );
    };

    // private _isFirstPage(): boolean {
    //     return this._getCurrentPageNumber() === 0;
    // }

    private _getCurrentPageNumber(): number {
        const itemsPerPage = this.props.config.itemsPerPage || 50;
        return (this.props.context.request.query && +this.props.context.request.query.skip / itemsPerPage) || 0;
    }

    private readonly _renderPagination = (): React.ReactNode | null => {
        const { config, context, data, resources } = this.props;
        const orderTemplate = data && data.orderTemplate && data.orderTemplate.result;
        if (!orderTemplate) {
            return null;
        }

        const fullUrl = this._getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 50;
        const currentPageNumber = this._getCurrentPageNumber();
        const skipCount = currentPageNumber * itemsPerPage;
        const totalItems = orderTemplate && orderTemplate.totalLines;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }

        return (
            <UncontrolledPagination
                className={`${this.className}__pagination`}
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
            />
        );
    };

    private _renderProductList(): IOrderTemplateLineViewProps[] {
        const {
            selectOneRowText,
            moreButtonText,
            viewProductDetailsText,
            priceFree,
            originalPriceText,
            currentPriceText,
            addOrderTemplateItemToBagButtonText,
            removeItemFromOrderTemplateButtonText,
            productDimensionTypeColor,
            productDimensionTypeSize,
            productDimensionTypeStyle,
            inputQuantityAriaLabel,
            deleteButtonText,
            incrementButtonAriaLabel,
            decrementButtonAriaLabel,
            unavailableProductErrorMessage,
            outOfStockProductErrorMessage,
            selectRowText,
            requestPriceTooltipText,
            requestPriceText,
            discontinuedText,
            minPurchaseQuantityIsText,
            maxPurchaseQuantityIsText,
            informationText
        } = this.props.resources;

        const { context, telemetry, data } = this.props;
        const { catalogs } = data;
        const { imageSettings } = this.props.config;

        return this.state.productSelection.map((line, index) => {
            let tempFlag = false;
            let tempMax = 0;
            this.state.refinedAtpData.map(
                (e: { parentId: string | undefined; TrafficLight: string; StatusCode: string; jsonResponce: string }) => {
                    if (line.fullProduct?.ProductDetails.ItemId === e.parentId) {
                        if (e.TrafficLight === 'Red') {
                            tempFlag = true;
                        } else if (e.TrafficLight === 'Green' && e.StatusCode === '204') {
                            const extensionProperties = JSON.parse(e.jsonResponce);
                            tempMax = extensionProperties?.Quantity;
                        }
                    }
                }
            );

            let manufacturer = '';
            this.state.attributeValues.length > 0 &&
                this.state.attributeValues?.map((item: { manufacturer: any; recId: number | undefined }) => {
                    if (line?.fullProduct?.ProductDetails?.RecordId! === item?.recId!) {
                        manufacturer = item?.manufacturer?.TextValue;
                    }
                });
            const manufacturerItemTypeNameValue = line.fullProduct?.ProductDetails?.ExtensionProperties?.find(item => {
                if (item.Key === 'ManufItemTypeName') {
                    return item;
                }
                return null;
            });
            const urlPath = formatUrlPath(manufacturer ?? '', manufacturerItemTypeNameValue?.Value?.StringValue! ?? '');
            const pathName = context?.actionContext.requestContext.url.requestUrl.pathname.split('/');
            const isAddingPathWithUrl = context?.actionContext.requestContext.app.config.addPathWithUrl ?? false;
            const productUrl =
                line &&
                (isAddingPathWithUrl
                    ? `${context?.actionContext.requestContext.url.requestUrl.origin}/${pathName[1]}/${urlPath}/${line.fullProduct?.ProductDetails.RecordId}.p`
                    : `${context?.actionContext.requestContext.url.requestUrl.origin}/${urlPath}/${line.fullProduct?.ProductDetails.RecordId}.p`);

            const currentProductProps: IOrderTemplateLinePropsExtended = {
                orderTemplateLine: line,
                productListLine: line.productListLine,
                price: line.fullProduct?.ProductPrice,
                error: line.error,
                shouldDisable: line.shouldDisable,
                context,
                isQuantityDisabledForUpdate: this.state.isQuantityDisabledForUpdate,
                productUrl: line.fullProduct ? productUrl : '',
                imageSettings,
                telemetry,
                kitDiscontinuedFlagValue: tempFlag,
                customer: this.props.data.customerInformation,
                moduleId: this.props.id,
                moduleTypeName: this.props.typeName,
                quantity: line.productListLine.Quantity!,
                handlers: {
                    onAddToBag: this._addItemToBag,
                    onRemoveItem: this._removeItemFromOrderTemplateLines,
                    onChangeQuantity: this._changeQuantity,
                    onSelect: this.onSelect.bind(this)
                },
                resources: {
                    selectOneRowText,
                    moreButtonText,
                    viewProductDetailsText,
                    productDimensionTypeColor,
                    productDimensionTypeSize,
                    productDimensionTypeStyle,
                    freePriceText: priceFree,
                    originalPriceText,
                    currentPriceText,
                    inputQuantityAriaLabel,
                    removeFromOrderTemplateLinesText: removeItemFromOrderTemplateButtonText,
                    addToBagButtonText: addOrderTemplateItemToBagButtonText,
                    deleteButtonText,
                    incrementButtonAriaLabel,
                    decrementButtonAriaLabel,
                    unavailableProductErrorMessage,
                    outOfStockProductErrorMessage
                },
                index,
                isMobile: this.isMobile,
                toggleDetailsDisplayed: this._toggleDetailsDisplayed,
                isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled(),
                catalogs: catalogs && catalogs.result,
                maxAtpValue: tempMax,
                selectRowText,
                requestPriceText,
                requestPriceTooltipText,
                discontinuedText,
                minPurchaseQuantityIsText,
                maxPurchaseQuantityIsText,
                informationText
            };

            return OrderTemplateLineView(currentProductProps)!;
        });
    }

    private readonly _toggleDetailsDisplayed = (props: IOrderTemplateLinePropsExtended): void => {
        let state = {
            isDetailsDisplayed: !this.state.isDetailsDisplayed
        };
        if (props) {
            state = {
                ...state,
                ...{
                    detailedProductView: OrderTemplateMobileLineView(props)
                }
            };
        }
        this.setState(state);
    };
    // This method is used to update the product url with the catalogId
    /* private readonly _updateProductUrlCatalog = (
        productDetails: SimpleProduct,
        context: ICoreContext,
        catalogId: number | undefined
    ): string => {
        const productUrl = getProductUrlSync(productDetails, context.actionContext, undefined);
        if (!StringExtensions.isNullOrWhitespace(productUrl) && catalogId !== undefined) {
            const absoluteUrl = new URL(productUrl, window.location.href);
            absoluteUrl.searchParams.set('catalogid', `${catalogId ?? 0}`);
            return absoluteUrl.href;
        }

        return productUrl;
    };*/

    // Dismiss alert when addingToCart/removeItem
    private readonly _onDismiss = () => {
        this.setState({
            isOpen: false
        });
    };

    private readonly _changeQuantity = async (itemKey: string, quantity: number, props?: IOrderTemplateLinePropsExtended) => {
        const { context, data, telemetry, config, resources } = this.props;
        const { orderTemplate } = data;

        if (context) {
            if (orderTemplate?.result) {
                const orderTemplateLinesData = orderTemplate.result.orderTemplateLines;
                if (orderTemplateLinesData) {
                    const orderLineIndex = orderTemplateLinesData.findIndex(
                        (lines: OrderTemplateLine) => lines.productListLine.LineId === itemKey
                    );
                    const newProductListLine = { ...orderTemplateLinesData[orderLineIndex] };
                    if (orderTemplateLinesData[orderLineIndex].productListLine.Quantity === quantity) {
                        return;
                    }
                    const prevQuantity = newProductListLine.productListLine.Quantity;
                    newProductListLine.productListLine.Quantity = quantity;
                    const { fullProduct, ...line } = newProductListLine;

                    // Update mobile modal
                    if (props) {
                        props.quantity = quantity;
                        this.setState({
                            detailedProductView: OrderTemplateMobileLineView(props)
                        });
                    }

                    this.setState({
                        isQuantityDisabledForUpdate: true
                    });

                    updateProductListLinesAsync({ callerContext: context.actionContext }, orderTemplate.result.productList.Id, [
                        line.productListLine
                    ])
                        .then(list => {
                            this.setState({
                                itemKey,
                                userMessage: '',
                                statusCssString: 'success',
                                messageGlyph: 'fas fa-check',
                                isQuantityDisabledForUpdate: false,
                                isOpen: false
                            });
                            if (list) {
                                const newLine = list.find(lines => lines.LineId === itemKey);
                                orderTemplateLinesData[orderLineIndex].productListLine = newLine!;
                                const updatedLines = [...orderTemplateLinesData];
                                updatedLines[orderLineIndex] = { fullProduct, productListLine: newLine! };
                                const updatedOrderTemplate = { ...orderTemplate.result, ProductListLines: updatedLines };
                                context.actionContext.update(
                                    new ActiveOrderTemplateLinesInput(context.actionContext.requestContext, config),
                                    updatedOrderTemplate
                                );
                            }
                        })
                        .catch(error => {
                            telemetry.warning('[order-template-lines] Unable to update product quantity.');
                            telemetry.error(error);
                            const userMessage = resources.editOrderTemplateLineQuantityError;
                            if (props) {
                                props && (props.productListLine.Quantity = prevQuantity);
                                this.setState({
                                    itemKey,
                                    userMessage,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isQuantityDisabledForUpdate: false,
                                    isOpen: true,
                                    detailedProductView: OrderTemplateMobileLineView(props)
                                });
                                //for closing alert
                                setTimeout(() => {
                                    this.setState({
                                        isOpen: false
                                    });
                                }, 5000);
                            } else {
                                newProductListLine.productListLine.Quantity = prevQuantity;
                                this.setState({
                                    itemKey,
                                    userMessage,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isQuantityDisabledForUpdate: false,
                                    isOpen: true
                                });
                                //for closing alert
                                setTimeout(() => {
                                    this.setState({
                                        isOpen: false
                                    });
                                }, 5000);
                            }
                        });
                }
            }
        }
    };

    private readonly _addItemToBag = async (
        product: SimpleProduct,
        quantity: number,
        itemKey: string,
        catalogId?: number | undefined
    ): Promise<void> => {
        const { context, data, resources, telemetry } = this.props;
        const { cart } = data;
        const { maxQuantityLimitText, addedToCartSuccessMessage, addedToCartFailureMessage, zeroPriceProductText } = resources;

        if (context) {
            if (cart?.result) {
                const prodATP = this.state.refinedAtpData?.find((item: { ItemId: string }) => item?.ItemId === itemKey)?.jsonResponce!;
                const prodATPParsed: { StatusCode: string; Quantity: any } = this.state.refinedAtpData && prodATP && JSON.parse(prodATP);
                const prodInCart = this.state.cartLinesState?.find((item: { ItemId: { toString: () => string } }) => {
                    if (item.ItemId?.toString() === itemKey.toString()) {
                        return item;
                    }
                    return null;
                });

                let addProductToCartInput: any;
                let isCallAPI = true;
                if (prodInCart && prodATPParsed?.StatusCode === '204') {
                    if (prodATPParsed?.Quantity === prodInCart?.Quantity || quantity > prodATPParsed?.Quantity - prodInCart?.Quantity) {
                        this.setState({
                            itemKey,
                            userMessage: maxQuantityLimitText
                                .replace('{curQuantity}', prodInCart?.Quantity?.toString()!)
                                .replace('{maxQuantity}', prodATPParsed?.Quantity?.toString()!),
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        }); //for closing alert
                        setTimeout(() => {
                            this.setState({ isOpen: false });
                        }, 5000);
                        isCallAPI = false;
                    } else if (quantity <= prodATPParsed?.Quantity - prodInCart?.Quantity) {
                        addProductToCartInput = {
                            product,
                            count: quantity,
                            catalogId
                        };
                    }
                } else {
                    addProductToCartInput = {
                        product,
                        count: quantity,
                        catalogId
                    };
                }
                if (isCallAPI === true) {
                    await cart.result
                        .addProductToCart(addProductToCartInput!)
                        .then((result: ICartActionResult) => {
                            if (result.status === 'SUCCESS') {
                                return getPriceForSelectedVariant(
                                    new PriceForSelectedVariantInput(
                                        product.RecordId,
                                        this.props.context.actionContext.requestContext.apiSettings.channelId,
                                        product
                                    ),
                                    this.props.context.actionContext
                                ).then(priceResult => {
                                    if (!priceResult) {
                                        return AsyncResult.reject('Failed to retrieve product price');
                                    }
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
                                    const isMiniCart = context.app.config.addToCartBehavior === 'showMiniCart';
                                    const success = this.state.addedLinesToPassArrSuccessfull;
                                    const lines = this.state.productSelection;
                                    const selectedLines = lines.filter(line => line.isChecked);
                                    if (selectedLines.length > 0) {
                                        success?.push({ itemKey, message: 'SUCCESS', product });
                                    }
                                    this.setState({
                                        itemKey,
                                        userMessage: addedToCartSuccessMessage,
                                        statusCssString: 'success',
                                        addToCartStatus: 'SUCCESS',
                                        messageGlyph: 'fas fa-check',
                                        isOpen: true,
                                        isItemAddedToOrderTemplateOpen: !isMiniCart,
                                        itemAddedToCartDialogData: {
                                            product,
                                            productPrice: priceResult,
                                            quantity
                                        },
                                        isDetailsDisplayed: false,
                                        addedLinesToPassArrSuccessfull: success
                                    });
                                    //for closing alert
                                    setTimeout(() => {
                                        this.setState({
                                            isOpen: false
                                        });
                                    }, 5000);
                                    return AsyncResult.resolve().then(() => {
                                        prodATPParsed?.StatusCode === '204' ? window.location.reload() : null;
                                    });
                                });
                            } else if (
                                result.status === 'FAILED' &&
                                result.errorDetails?.LocalizedMessage === 'A validation error occurred.'
                            ) {
                                this.setState({
                                    itemKey,
                                    userMessage: zeroPriceProductText,
                                    statusCssString: 'danger',
                                    addToCartStatus: 'FAILEDBYZEROPRICE',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isOpen: true
                                }); //for closing alert
                                setTimeout(() => {
                                    this.setState({
                                        isOpen: false
                                    });
                                }, 5000);
                                return AsyncResult.resolve();
                            } else {
                                switch (result.substatus) {
                                    case 'MAXQUANTITY':
                                        if (context.app.config.maxQuantityForCartLineItem) {
                                            const cartLineQuantity: number | undefined =
                                                cart?.result?.cart.CartLines &&
                                                cart.result?.cart.CartLines.find(result => result.ProductId === product.RecordId)?.Quantity;
                                            const defaultMessage = resources.onlyAddLimitedItemToYourShoppingBagErrorMessage.replace(
                                                '{maxQuantityForCartLineItem}',
                                                context.app.config.maxQuantityForCartLineItem.toString()
                                            );
                                            this.setState({
                                                itemKey,
                                                userMessage:
                                                    cartLineQuantity !== undefined
                                                        ? maxQuantityLimitText
                                                              .replace('{curQuantity}', cartLineQuantity.toString())
                                                              .replace(
                                                                  '{maxQuantity}',
                                                                  context.app.config.maxQuantityForCartLineItem.toString()
                                                              ) || defaultMessage
                                                        : defaultMessage,
                                                statusCssString: 'danger',
                                                messageGlyph: 'fas fa-exclamation-triangle',
                                                isOpen: true
                                            }); //for closing alert
                                            setTimeout(() => {
                                                this.setState({
                                                    isOpen: false
                                                });
                                            }, 5000);
                                            return AsyncResult.resolve();
                                        } else {
                                            const defaultMessage = resources.maxQuantityIsUndefinedErrorMessage;
                                            this.setState({
                                                itemKey,
                                                userMessage: defaultMessage,
                                                statusCssString: 'danger',
                                                messageGlyph: 'fas fa-exclamation-triangle',
                                                isOpen: true
                                            }); //for closing alert
                                            setTimeout(() => {
                                                this.setState({
                                                    isOpen: false
                                                });
                                            }, 5000);
                                            return AsyncResult.resolve();
                                        }
                                    case 'INVOICEINCART': {
                                        this.setState({
                                            itemKey,
                                            userMessage: result.errorDetails?.LocalizedMessage || addedToCartFailureMessage,
                                            statusCssString: 'danger',
                                            messageGlyph: 'fas fa-exclamation-triangle',
                                            isOpen: true
                                        });
                                        //for closing alert
                                        setTimeout(() => {
                                            this.setState({
                                                isOpen: false
                                            });
                                        }, 5000);
                                        return AsyncResult.resolve();
                                    }
                                    default: {
                                        telemetry.debug('[order-template-lines] Unable to Update Cart Line from the order template lines');
                                        this.setState({
                                            itemKey,
                                            userMessage: addedToCartFailureMessage,
                                            statusCssString: 'danger',
                                            messageGlyph: 'fas fa-exclamation-triangle',
                                            isOpen: true
                                        });
                                        //for closing alert
                                        setTimeout(() => {
                                            this.setState({
                                                isOpen: false
                                            });
                                        }, 5000);
                                        return AsyncResult.resolve();
                                    }
                                }
                            }
                        })
                        .catch((error: Error) => {
                            telemetry.warning(error.message);
                            telemetry.debug('[order-template-lines] Error trying to Update Cart Line from the order template lines');
                            if (StringExtensions.isNullOrWhitespace(error.message)) {
                                this.setState({
                                    itemKey,
                                    userMessage: error.message,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isOpen: true
                                });
                                //for closing alert
                                setTimeout(() => {
                                    this.setState({
                                        isOpen: false
                                    });
                                }, 5000);
                            } else {
                                this.setState({
                                    itemKey,
                                    userMessage: addedToCartFailureMessage,
                                    statusCssString: 'danger',
                                    addToCartStatus: 'FAILED',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isOpen: true
                                });
                                //for closing alert
                                setTimeout(() => {
                                    this.setState({
                                        isOpen: false
                                    });
                                }, 5000);
                            }
                        });
                }
            }
        }
    };

    private readonly _removeItemFromOrderTemplateLines = async (line: IOrderTemplateLineViewModel): Promise<void | undefined> => {
        const { context, data, telemetry } = this.props;
        const { orderTemplate } = data;

        if (context && context.actionContext) {
            const maxWaitTimeInMilliSeconds: number = 1000;
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplate?.result && line) {
                const featureStates = await getFeatureState(createGetFeatureStateInput(context.actionContext), context.actionContext);
                let isMultipleCatalogFeatureEnabledInHq = false;
                if (ArrayExtensions.hasElements(featureStates)) {
                    isMultipleCatalogFeatureEnabledInHq =
                        featureStates.find(
                            (featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                        )?.IsEnabled || false;
                }
                const catalogId = getCatalogId(context.actionContext.requestContext);
                if (isMultipleCatalogFeatureEnabledInHq) {
                    line.productListLine.CatalogId = catalogId;
                }
                removeProductListLinesAsync({ callerContext: context.actionContext }, line.productListLine.ProductListId!, [
                    line.productListLine
                ])
                    .then(async () => {
                        // eslint-disable-next-line no-promise-executor-return -- fix
                        await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));
                        window.location.reload();
                    })
                    .catch(error => {
                        telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                        telemetry.error(error);
                    });
            } else {
                telemetry.warning(
                    '[order-template-lines] Unable remove items from the order template lines user token or lineItemID was not found'
                );
            }
        }
    };

    private readonly _renameTemplate = async (_template: ProductList): Promise<void> => {
        window.location.reload();
        return Promise.resolve();
    };

    private readonly _removeTemplate = () => {
        const { context, data, telemetry, resources } = this.props;
        const { orderTemplate } = data;

        if (context && context.actionContext) {
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplate && orderTemplate.result) {
                deleteAsync({ callerContext: context.actionContext }, orderTemplate.result.productList)
                    .then()
                    .catch((error: string) => {
                        telemetry.warning('[order-template] Unable to delete order template.');
                        telemetry.error(error);
                        this.setState({
                            itemKey: orderTemplate.result?.productList.Id || '',
                            userMessage: resources.deleteOrderTemplateFailedText,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        });
                        //for closing alert
                        setTimeout(() => {
                            this.setState({
                                isOpen: false
                            });
                        }, 5000);
                    });
                const newLink = getUrlSync('ordertemplatelist', this.props.context.actionContext);
                if (newLink) {
                    window.location.assign(newLink);
                }
            } else {
                telemetry.warning('[order-template] Failed to delete order template. Order template was not found');
            }
        } else {
            telemetry.warning('[order-template] Failed to delete order template.');
        }
    };

    private _updateViewport(): void {
        this._viewport = this._getViewport();
    }

    private readonly _getViewport = (): GridSize => {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'xs';
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className={`${this.props.config.className}__pagination-left`} aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className={`${this.props.config.className}__pagination-right`} aria-hidden='true' />
            </div>
        );
    };
    private readonly _atpData = async () => {
        const KRVATPLeadTimeDate: any[] = [];
        //Create date according to API
        const objectDate = new Date();
        const day = objectDate.getDate();
        const month = objectDate.getMonth();
        const year = objectDate.getFullYear();
        const requestedDate = `${day}/${month + 1}/${year}`;

        this.state.productSelection.map(product => {
            //for bundle products
            if (product.fullProduct === undefined) {
                return;
            } else if (product.fullProduct?.ProductDetails.Components?.length! > 0) {
                //adding parents Ids
                KRVATPLeadTimeDate.push({
                    itemID: product.fullProduct?.ProductDetails.ItemId,
                    requestedDate: requestedDate
                });
                //adding childs into payload
                product.fullProduct?.ProductDetails?.Components!.map((e: any) => {
                    KRVATPLeadTimeDate.push({
                        itemID: e.ItemId,
                        requestedDate: requestedDate
                    });
                });
            } else {
                //for standard product
                KRVATPLeadTimeDate.push({
                    itemID: product.fullProduct?.ProductDetails.ItemId,
                    requestedDate: requestedDate
                });
            }
        });
        const ctx = { callerContext: this.props.context.actionContext };
        try {
            const response = await kRV_GetMultipleATPLeadTimeDateAsync(ctx, KRVATPLeadTimeDate);
            const tempArray: any[] = [];
            const finalArray: any[] = [];

            //Sorting the response of ATP traffic lights
            this.setState({ atpDataForKits: response }, () => {
                this.state.atpDataForKits?.ATPTrafficLights?.map(
                    (item: { ItemId: any; StatusCode: any; TrafficLight: any; JsonResponseObject: any }) => {
                        tempArray.push({
                            ItemId: item.ItemId,
                            StatusCode: item.StatusCode,
                            TrafficLight: item.TrafficLight,
                            jsonResponce: item?.JsonResponseObject
                        });
                    }
                );
                //adding parentID back with each entry
                tempArray.map(i => {
                    this.state.productSelection.map(j => {
                        //condition for parent
                        if (i.ItemId === j.fullProduct?.ProductDetails.ItemId) {
                            finalArray.push({
                                ...i,
                                parentId: i.ItemId
                            });
                        }
                        //condition for childs
                        else if (j.fullProduct?.ProductDetails.Components?.length! > 0) {
                            j.fullProduct?.ProductDetails.Components?.map(k => {
                                if (k.ItemId === i.ItemId) {
                                    finalArray.push({
                                        ...i,
                                        parentId: j.fullProduct?.ProductDetails.ItemId
                                    });
                                }
                            });
                        }
                    });
                });
            });
            this.setState({ refinedAtpData: finalArray });
        } catch (err) {
            console.error(err, 'ERR');
        }
    };
    private readonly _attributesData = async () => {
        if (this.state.productSelection) {
            const res = [];
            const productManufacturarAttribute = this.props.context?.app?.config?.productManufacturarAttribute;
            for (const salesLine of this.state.productSelection) {
                const productAttributes = await getAttributeValuesAsync(
                    { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                    salesLine.fullProduct?.ProductDetails.RecordId!,
                    this.props.context.request.apiSettings.channelId,
                    this.props.context.request.apiSettings.catalogId
                );
                const manufacturer = productAttributes?.find(item => item.Name === productManufacturarAttribute);
                res.push({ recId: salesLine.fullProduct?.ProductDetails.RecordId, manufacturer });
            }
            this.setState({ attributeValues: res });
        }
    };
    private _refiningOrderTemplateLines = (orderTemplate: OrderTemplate, customerInformation: AsyncResult<Customer> | undefined) => {
        const tempArray: any[] = [];
        const custom = orderTemplate;

        //Finding Items with Red Traffic Light
        this.state.refinedAtpData.map((item: { TrafficLight: string; parentId: any; jsonResponce: any }) => {
            const obj = JSON.parse(item?.jsonResponce);
            if (item?.TrafficLight === 'Red' || obj?.IsRemovedFromCart === 'Yes') {
                tempArray.push({
                    ItemId: item?.parentId
                });
            }
        });
        //Removing duplicate Entries in case of multiple Red lights
        const itemsToBeRemoved = tempArray.reduce((unique, current) => {
            const existingItem = unique.find((item: { ItemId: any }) => item.ItemId === current.ItemId);
            if (!existingItem) {
                unique.push(current);
            }
            return unique;
        }, []);
        //Removing Discotinued kit products
        const filteredOrderTemplateLines = orderTemplate?.orderTemplateLines.filter(line => {
            return !itemsToBeRemoved.some(
                (item: { ItemId: string | undefined }) => item.ItemId === line.fullProduct?.ProductDetails.ItemId
            );
        });
        if (filteredOrderTemplateLines?.length) {
            if (custom?.orderTemplateLines !== undefined) {
                custom.orderTemplateLines = filteredOrderTemplateLines;
                custom.totalLines = filteredOrderTemplateLines.length;
                this.setState(
                    {
                        refinedOrderTemplateLines: custom
                    },
                    async () => {
                        //getting active prices
                        const activePricesResult = await this._getActivePrices(
                            customerInformation,
                            this.state.refinedOrderTemplateLines.orderTemplateLines
                        );
                        if (activePricesResult.length) {
                            this.setState({ activePrices: activePricesResult }, () => {
                                let orderTemplateLinesWithoutRequestPriceProducts: any[] = [];
                                if (this.state.activePrices.length) {
                                    //fetching order Template line which are not request price products
                                    orderTemplateLinesWithoutRequestPriceProducts = this.state.refinedOrderTemplateLines.orderTemplateLines.map(
                                        (item: { fullProduct: { ProductDetails: { ItemId: any } } }) => {
                                            const activePriceOfLine = this.state.activePrices.find((e: { ItemId: any }) => {
                                                if (e.ItemId === item.fullProduct?.ProductDetails?.ItemId) {
                                                    return e;
                                                }
                                                return null;
                                            });
                                            if (!isRequestPrice(activePriceOfLine, customerInformation?.result?.LineDiscountGroup!)) {
                                                return item;
                                            } else {
                                                return;
                                            }
                                        }
                                    );
                                }
                                //removing undefined items
                                orderTemplateLinesWithoutRequestPriceProducts = orderTemplateLinesWithoutRequestPriceProducts.filter(
                                    item => item !== undefined
                                );
                                const custom = this.state.refinedOrderTemplateLines;
                                custom.orderTemplateLines = orderTemplateLinesWithoutRequestPriceProducts;
                                custom.totalLines = orderTemplateLinesWithoutRequestPriceProducts.length;
                                //updating template lines after removing Request Price products
                                this.setState({
                                    refinedOrderTemplateLines: custom
                                });
                            });
                        }
                    }
                );
            }
        }
    };

    private _getActivePrices = async (customer: AsyncResult<Customer> | undefined, products: any[]) => {
        const recordIds = products?.map((value: any) => value?.fullProduct?.ProductDetails?.RecordId);
        return getActivePricesAsync(
            { callerContext: this.props.context.actionContext },
            {
                ChannelId: this.props.context.request.apiSettings.channelId,
                CatalogId: this.props.context.request.apiSettings.catalogId
            },
            recordIds,
            new Date(),
            customer?.result?.AccountNumber ?? null,
            undefined,
            true
        );
    };
}

export default OrderTemplateLines;
